import { useEffect, useState } from 'react';
import { useBlocker, useParams } from 'react-router-dom';
import { KeyRound, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button, ConfirmationModal, EmptyState, Skeleton } from '../ui';
import { useMutation, useQuery } from '../gql/hooks';
import {
  APPLICATION_QUERY,
  CREATE_API_KEY_MUTATION,
  REVOKE_API_KEY_MUTATION,
  ROTATE_API_KEY_MUTATION,
  SET_API_KEY_OVERRIDE_MUTATION,
  type ApplicationQueryResult,
  type CreateApiKeyMutationResult,
  type CreateApiKeyMutationVariables,
  type RevokeApiKeyMutationResult,
  type RevokeApiKeyMutationVariables,
  type RotateApiKeyMutationResult,
  type RotateApiKeyMutationVariables,
  type SetApiKeyOverrideMutationResult,
  type SetApiKeyOverrideMutationVariables,
} from '../gql/operations';
import { ApiKeyStatus, OverrideMode, type ApiKey } from '../gql/types';
import { PageHeader } from '../features/shared/PageHeader';
import { UsageTallyCard } from '../features/usage/UsageTallyCard';
import { ApiKeyStatusBadge } from '../features/shared/StatusBadge';
import { RawKeyRevealModal } from '../features/keys/RawKeyRevealModal';
import { OverrideEditor, type OverrideSelection } from '../features/keys/OverrideEditor';
import { formatDate, formatRelative } from '../lib/format';

// Application detail / keys view (US-8.1.1, US-8.1.2). Browse keys; issue a key (copy-once
// reveal), revoke + rotate (confirm-gated), and edit the routing override inline.

interface RevealState {
  rawKey: string;
  keyLabel: string;
  rotated: boolean;
}

function KeyCard({
  apiKey,
  onRevoke,
  onRotate,
  onSaveOverride,
  overrideSaving,
}: {
  apiKey: ApiKey;
  onRevoke: (key: ApiKey) => void;
  onRotate: (key: ApiKey) => void;
  onSaveOverride: (key: ApiKey, selection: OverrideSelection) => void;
  overrideSaving: boolean;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isRevoked = apiKey.status === ApiKeyStatus.Revoked;

  return (
    <div
      className={`rounded-2xl border p-5 transition-all duration-300 ${
        isRevoked ? 'opacity-60' : ''
      } ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 items-center justify-center rounded-xl ${
              isRevoked
                ? isDark
                  ? 'bg-white/[0.04] text-white/40'
                  : 'bg-slate-100 text-slate-400'
                : 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/20'
            }`}
          >
            <KeyRound className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <code
                className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {apiKey.maskedKey}
              </code>
              <ApiKeyStatusBadge status={apiKey.status} />
            </div>
            <p className={`mt-0.5 text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
              Created {formatDate(apiKey.insertedAt)}
              {' · '}
              {apiKey.lastUsedAt
                ? `last used ${formatRelative(apiKey.lastUsedAt)}`
                : 'never used'}
            </p>
          </div>
        </div>

        {!isRevoked && (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRotate(apiKey)}
              leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
            >
              Rotate
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onRevoke(apiKey)}
              leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
            >
              Revoke
            </Button>
          </div>
        )}
      </div>

      {apiKey.scopes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {apiKey.scopes.map((scope) => (
            <span
              key={scope}
              className={`rounded-md px-2 py-0.5 font-mono text-[11px] ${
                isDark ? 'bg-white/[0.04] text-white/60' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {scope}
            </span>
          ))}
        </div>
      )}

      {!isRevoked && (
        <div className="mt-4">
          <OverrideEditor
            mode={apiKey.overrideMode}
            pinnedProvider={apiKey.pinnedProvider ?? null}
            loading={overrideSaving}
            onSave={(selection) => onSaveOverride(apiKey, selection)}
          />
        </div>
      )}
    </div>
  );
}

export function ApplicationDetailPage() {
  const { clientId = '', applicationId = '' } = useParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const { data, loading, error, refetch } = useQuery<
    ApplicationQueryResult,
    { id: string }
  >(APPLICATION_QUERY, { id: applicationId }, { enabled: Boolean(applicationId) });

  const createKey = useMutation<
    CreateApiKeyMutationResult,
    CreateApiKeyMutationVariables
  >(CREATE_API_KEY_MUTATION);
  const revokeKey = useMutation<
    RevokeApiKeyMutationResult,
    RevokeApiKeyMutationVariables
  >(REVOKE_API_KEY_MUTATION);
  const rotateKey = useMutation<
    RotateApiKeyMutationResult,
    RotateApiKeyMutationVariables
  >(ROTATE_API_KEY_MUTATION);
  const setOverride = useMutation<
    SetApiKeyOverrideMutationResult,
    SetApiKeyOverrideMutationVariables
  >(SET_API_KEY_OVERRIDE_MUTATION);

  // The raw secret lives ONLY in this transient state and is cleared on acknowledge.
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKey | null>(null);
  const [savingOverrideId, setSavingOverrideId] = useState<string | null>(null);

  const application = data?.application ?? null;
  const keys = application?.apiKeys ?? [];

  // A rawKey is returned exactly once and only ever shown in the reveal modal. If the operator
  // navigates away while a key is mid-issue/rotate, the key would be created on the backend but
  // the reveal would never mount — forcing an immediate rotate. Block in-app navigation while a
  // secret-producing mutation is in flight so the reveal is guaranteed to appear.
  const issuingKey = createKey.loading || rotateKey.loading;
  const blocker = useBlocker(issuingKey);

  // Once the mutation settles, release a navigation that was blocked mid-flight (the reveal has
  // since mounted, or the mutation failed and surfaced an error) so the operator isn't stuck.
  useEffect(() => {
    if (!issuingKey && blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [issuingKey, blocker]);

  // Each handler swallows the rejection after the hook has recorded it in `.error` (rendered
  // below), so a failed Issue/Revoke/Rotate surfaces a visible error instead of silently
  // dropping. On failure we deliberately do NOT advance the flow (no reveal, no dialog close,
  // no refetch) so the operator can read the error and retry.
  const handleIssue = async () => {
    try {
      const result = await createKey.mutate({
        input: { applicationId, overrideMode: OverrideMode.Auto },
      });
      setReveal({
        rawKey: result.createApiKey.rawKey,
        keyLabel: result.createApiKey.apiKey.keyPrefix,
        rotated: false,
      });
      refetch();
    } catch {
      // Error is in createKey.error; the banner below renders it.
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeKey.mutate({ id: revokeTarget.id });
      setRevokeTarget(null);
      refetch();
    } catch {
      // Error is in revokeKey.error; keep the confirm dialog open so the operator sees it.
    }
  };

  const handleConfirmRotate = async () => {
    if (!rotateTarget) return;
    try {
      const result = await rotateKey.mutate({ id: rotateTarget.id });
      setRotateTarget(null);
      setReveal({
        rawKey: result.rotateApiKey.rawKey,
        keyLabel: result.rotateApiKey.apiKey.keyPrefix,
        rotated: true,
      });
      refetch();
    } catch {
      // Error is in rotateKey.error; keep the confirm dialog open so the operator sees it.
    }
  };

  const handleSaveOverride = async (key: ApiKey, selection: OverrideSelection) => {
    setSavingOverrideId(key.id);
    try {
      await setOverride.mutate({
        id: key.id,
        overrideMode: selection.overrideMode,
        pinnedProvider: selection.pinnedProvider,
      });
      refetch();
    } finally {
      setSavingOverrideId(null);
    }
  };

  const acknowledgeReveal = () => setReveal(null);

  // Surface whichever key mutation most recently failed. The hooks reset `.error` to null at the
  // start of each `mutate`, so at most one is set at a time; pick the first present.
  const mutationError =
    createKey.error ??
    revokeKey.error ??
    rotateKey.error ??
    setOverride.error ??
    null;

  return (
    <div>
      <PageHeader
        title={application?.name ?? 'Application'}
        description="API keys for this application. Issuing a key reveals its secret exactly once."
        breadcrumbs={[
          { label: 'Clients', to: '/clients' },
          {
            label: application?.client.name ?? 'Client',
            to: `/clients/${clientId}`,
          },
          { label: application?.name ?? 'Application' },
        ]}
        actions={
          <Button
            variant="primary"
            onClick={() => void handleIssue()}
            loading={createKey.loading}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Issue key
          </Button>
        }
      />

      {application && (
        <div className="mb-6">
          <UsageTallyCard
            filter={{ applicationId: application.id }}
            title="Application usage"
            subtitle="Aggregated across all API keys for this application"
          />
        </div>
      )}

      {error && (
        <div
          role="alert"
          className={`mb-6 rounded-xl p-4 ${
            isDark ? 'border border-rose-500/20 bg-rose-500/10' : 'border border-rose-200 bg-rose-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
            Couldn’t load keys: {error.message}
          </p>
        </div>
      )}

      {mutationError && (
        <div
          role="alert"
          data-testid="mutation-error"
          className={`mb-6 rounded-xl p-4 ${
            isDark ? 'border border-rose-500/20 bg-rose-500/10' : 'border border-rose-200 bg-rose-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-rose-300' : 'text-rose-700'}`}>
            That action couldn’t be completed: {mutationError.message}
          </p>
        </div>
      )}

      {blocker.state === 'blocked' && (
        <div
          role="status"
          aria-live="polite"
          data-testid="nav-blocked-notice"
          className={`mb-6 rounded-xl p-4 ${
            isDark ? 'border border-amber-500/20 bg-amber-500/10' : 'border border-amber-200 bg-amber-50'
          }`}
        >
          <p className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
            Hold on — your new secret is being generated. Stay on this page so you can copy it;
            it’s shown only once.
          </p>
        </div>
      )}

      {loading && (
        <div className="space-y-4" data-testid="keys-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="block" height={160} />
          ))}
        </div>
      )}

      {!loading && !error && keys.length === 0 && (
        <EmptyState
          icon={<KeyRound className="h-16 w-16" aria-hidden="true" />}
          title="No API keys yet"
          description="Issue a key to let this application call the gateway. The secret is shown only once."
          action={{ label: 'Issue first key', onClick: () => void handleIssue() }}
        />
      )}

      {!loading && keys.length > 0 && (
        <ul className="space-y-4">
          {keys.map((key) => (
            <li key={key.id}>
              <KeyCard
                apiKey={key}
                onRevoke={setRevokeTarget}
                onRotate={setRotateTarget}
                onSaveOverride={(k, sel) => void handleSaveOverride(k, sel)}
                overrideSaving={savingOverrideId === key.id}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Copy-once reveal — the only place the raw secret is shown. */}
      <RawKeyRevealModal
        isOpen={reveal !== null}
        rawKey={reveal?.rawKey ?? ''}
        keyLabel={reveal?.keyLabel}
        rotated={reveal?.rotated ?? false}
        onAcknowledge={acknowledgeReveal}
      />

      {/* Confirm-gated revoke. */}
      <ConfirmationModal
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => void handleConfirmRevoke()}
        variant="danger"
        title="Revoke this key?"
        confirmLabel="Revoke key"
        loading={revokeKey.loading}
        description={
          <>
            Revoking <code className="font-mono">{revokeTarget?.maskedKey}</code> immediately
            stops it from authenticating. This cannot be undone.
          </>
        }
      />

      {/* Confirm-gated rotate. */}
      <ConfirmationModal
        isOpen={rotateTarget !== null}
        onClose={() => setRotateTarget(null)}
        onConfirm={() => void handleConfirmRotate()}
        variant="default"
        title="Rotate this key?"
        confirmLabel="Rotate key"
        loading={rotateKey.loading}
        description={
          <>
            Rotating issues a new secret for{' '}
            <code className="font-mono">{rotateTarget?.maskedKey}</code> and invalidates the
            old one. You’ll see the new secret once.
          </>
        }
      />
    </div>
  );
}
